import type { ReactNode } from "react";
import { Button } from "./Button";
import { Callout } from "./Callout";
import { VisuallyHidden } from "./VisuallyHidden";
import { isNetworkError, isUnauthorized } from "../api/client";
import type { ApiError } from "../api/client";
import type { ResourceState } from "../hooks/useApiResource";

/**
 * The loading and error states of one desk panel, rendered the same way everywhere.
 *
 * A panel that invented its own spinner or its own error wording would be one more place
 * for these to drift apart, and the seller desk shows several panels at once — three
 * differently-worded failures on one screen read as three different outages.
 *
 * **An expired session is not an error.** Core re-checks the session, the Google identity
 * behind it and the seller's status on *every* `/v1/seller/*` request (`contracts/API.md`
 * § Seller authorization), so a `401` here means the desk outlived its session — a seller
 * was disabled, an identity was re-bound, or the cookie simply expired while the tab sat
 * open. That needs sign-in, not a retry, so it offers the one action that helps.
 * `onSessionEnded` runs the app's session read again, which lands on the sign-in screen
 * because there is no longer a session to find.
 *
 * The wording is this repo's, not core's. Core's messages are contract text written for
 * every caller; some of them describe conditions a founder can do nothing about.
 */
export function AsyncPanel<T>({
  state,
  reload,
  onSessionEnded,
  loadingLabel,
  skeleton,
  errorTitle,
  children,
}: {
  state: ResourceState<T>;
  reload: () => void;
  onSessionEnded: () => void;
  /** What is loading, for assistive technology: "Loading your promotional credit". */
  loadingLabel: string;
  skeleton: ReactNode;
  errorTitle: string;
  /**
   * Receives the data and the local instant it arrived. The arrival time is what lets a
   * countdown be measured against core's clock rather than the browser's (canon § 8); a
   * panel with no countdown simply ignores the second argument.
   */
  children: (data: T, receivedAtMs: number) => ReactNode;
}) {
  if (state.status === "loading") {
    return (
      <div role="status" aria-busy="true">
        <VisuallyHidden>{loadingLabel}</VisuallyHidden>
        {skeleton}
      </div>
    );
  }

  if (state.status === "error") {
    if (isUnauthorized(state.error)) {
      return (
        <Callout
          tone="warning"
          title="Your Dealbid session has ended"
          actions={
            <Button variant="secondary" onClick={onSessionEnded}>
              Sign in again
            </Button>
          }
        >
          Sign in with Google again to get back to your desk. Nothing you have done here
          was lost.
        </Callout>
      );
    }

    return (
      <Callout
        tone="error"
        title={errorTitle}
        actions={
          <Button variant="secondary" onClick={reload}>
            Try again
          </Button>
        }
      >
        {describe(state.error)}
      </Callout>
    );
  }

  return <>{children(state.data, state.receivedAtMs)}</>;
}

function describe(error: ApiError): string {
  // A malformed body also carries a null status — the request completed and the answer was
  // unreadable — so the code is checked first. Telling a founder their connection dropped
  // when it did not would send them to restart a router that is working.
  if (error.code === null && isNetworkError(error)) {
    return "Dealbid couldn't be reached. Your connection may have dropped.";
  }
  return "Dealbid didn't answer as expected. This is on Dealbid's side, not yours.";
}
